import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Col, FormGroup, Input, Label, Row, Spinner, Table } from "reactstrap";

import { useCompanies } from "hooks/companies";
import { useToast } from "../../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "../../../../components/PageHero";
import FilterBand from "../../../../components/FilterBand";
import MetricCard from "../../../../components/MetricCard";
import MetricCardGroup from "../../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../../components/ShellCard";
import SortHeader from "../../../../components/SortHeader";
import TablePagination from "../../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../../components/autopilot/styles";
import { sortRows, toggleSort, paginate } from "../../../../utils/tableHelpers";
import { indicadoresResposta, listarLeiloes } from "services/leilaoFreteApi";
import NovoLeilaoModal from "./NovoLeilaoModal";
import LeilaoDetalheModal from "./LeilaoDetalheModal";
import EscolherCotacaoModal from "./EscolherCotacaoModal";
import { ESTADOS_ORDEM, ESTADO_UI, cidadeUf, countdown, fmtBRL, fmtDataHora, matchesSearch, offsetDoServidor } from "./regras";
import "./styles.css";

// GoBID › Operação › Leilões (H3 PO1-3084, épico PO1-3076). Molde: a Fila de
// Fretes (kit PO1-2723), com o seletor de empresa do cabeçalho (useCompanies).
// Deep-links: ?abrir=<leilaoId> (detalhe) e ?cotacao=<embarqueId> (criar).

const PAGE_SIZES = [10, 20, 50, 100];

const CARDS = [
  { key: "aberta", label: "Abertas", campo: "abertas", icon: "tim-icons icon-time-alarm", tone: "info", helper: "janela em andamento" },
  { key: "encerrada", label: "Aguardando escolha", campo: "aguardandoEscolha", icon: "tim-icons icon-trophy", tone: "warning", helper: "janela fechou com lance" },
  { key: "adjudicada", label: "Adjudicadas (30d)", campo: "adjudicadas30d", icon: "tim-icons icon-check-2", tone: "success", helper: "vencedora virou cotação" },
  { key: "deserta", label: "Desertas (30d)", campo: "desertas30d", icon: "tim-icons icon-simple-remove", tone: "neutral", helper: "sem lance; pode reabrir" },
];

// PO1-3528 (épico PO1-3527): taxa de resposta por origem do convidado, para
// medir o efeito do perfil do embarcador no convite de quem é de fora.
const RESPOSTA_DIAS = 90;
const RESPOSTA_CARDS = [
  { key: "carteira", label: "Da carteira", icon: "tim-icons icon-single-02", tone: "info" },
  { key: "fora", label: "Fora da carteira", icon: "tim-icons icon-world", tone: "success" },
];

export function textoResposta(grupo) {
  if (!grupo || !grupo.convidados) return { value: "—", helper: "Sem convites no período" };
  const taxa = grupo.taxa == null ? Math.round((grupo.responderam / grupo.convidados) * 1000) / 10 : grupo.taxa;
  return { value: `${String(taxa).replace(".", ",")}%`, helper: `${grupo.responderam} de ${grupo.convidados} responderam` };
}

const Leiloes = () => {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const history = useHistory();
  const empresaId = empresa && empresa.value;

  const [dados, setDados] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [estadoFiltro, setEstadoFiltro] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [sort, setSort] = useState({ field: "numero", dir: "desc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [, setTick] = useState(0);
  const [detalheId, setDetalheId] = useState(null);
  const [novoEmbarqueId, setNovoEmbarqueId] = useState(null);
  const [escolherAberto, setEscolherAberto] = useState(false);
  const [resposta, setResposta] = useState(null);

  // Deep-links lidos uma vez no mount (padrão de Minhas Cotações).
  const abrirRef = useRef(new URLSearchParams(history.location.search).get("abrir"));
  const cotacaoRef = useRef(new URLSearchParams(history.location.search).get("cotacao"));

  const carregar = useCallback(async () => {
    if (!empresaId || !hasEmpresaSelection) return;
    setLoading(true);
    setError(null);
    // A taxa de resposta é informativa: falha dela não derruba a lista.
    indicadoresResposta(empresaId, { dias: RESPOSTA_DIAS })
      .then(setResposta)
      .catch(() => setResposta(null));
    try {
      const r = await listarLeiloes(empresaId, { limite: 500 });
      setDados(r);
      setOffset(offsetDoServidor(r.serverNow));
    } catch (requestError) {
      const msg = (requestError.response && requestError.response.data && requestError.response.data.error) || "Erro ao carregar os leilões.";
      setError(msg);
      addToast({ place: "tr", type: "danger", title: "Leilões", description: msg, icon: "tim-icons icon-alert-circle-exc" });
    } finally {
      setLoading(false);
    }
  }, [empresaId, hasEmpresaSelection, addToast]);

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setDados(null);
      return;
    }
    carregar();
  }, [empresaId, hasEmpresaSelection, carregar]);

  useEffect(() => {
    const abrir = abrirRef.current;
    const cotacao = cotacaoRef.current;
    if (!empresaId) return;
    if (abrir) {
      abrirRef.current = null;
      setDetalheId(abrir);
    } else if (cotacao) {
      cotacaoRef.current = null;
      setNovoEmbarqueId(cotacao);
    }
    if (abrir || cotacao) history.replace({ pathname: history.location.pathname, search: "" });
  }, [empresaId, history]);

  // Countdown da listagem: tick por segundo.
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const itens = (dados && dados.itens) || [];
  const cards = (dados && dados.cards) || {};

  const itensFiltrados = useMemo(
    () => itens.filter((i) => (!estadoFiltro || i.estado === estadoFiltro) && matchesSearch(i, searchTerm)),
    [itens, estadoFiltro, searchTerm],
  );
  const linhas = useMemo(
    () =>
      itensFiltrados.map((i) => ({
        ...i,
        destinoTxt: cidadeUf(i.carga && i.carga.destino),
        menorLance: i.vigentes && i.vigentes.length ? i.vigentes[0].valor : null,
        menorLanceNome: i.vigentes && i.vigentes.length ? i.vigentes[0].nome : null,
        fechaEmTs: new Date(i.fechaEm).getTime(),
      })),
    [itensFiltrados],
  );
  const ordenados = useMemo(() => sortRows(linhas, sort), [linhas, sort]);

  if (!hasEmpresaSelection) {
    return (
      <div className="content ff-page">
        <SpecificEmpresaRequiredNotice title="Selecione um embarcador no cabeçalho" message="Esta tela usa o embarcador selecionado no topo para listar os leilões de frete." />
      </div>
    );
  }

  const paginaAtual = paginate(ordenados, page, pageSize);
  const handleSort = (field) => setSort((current) => toggleSort(current, field));
  const toggleEstado = (key) => {
    setEstadoFiltro((cur) => (cur === key ? null : key));
    setPage(1);
  };

  const columns = [
    { field: "numero", label: "Nº" },
    { field: "numeroCotacao", label: "Cotação" },
    { field: "destinoTxt", label: "Destino" },
    { field: "precoAlvo", label: "Alvo", numeric: true },
    { field: "menorLance", label: "Menor lance", numeric: true },
    { field: "qtdTransportadorasComLance", label: "Lances / convidadas", numeric: true },
    { field: "fechaEmTs", label: "Janela" },
    { field: "estado", label: "Estado" },
  ];

  return (
    <div className="content ff-page">
      <PageHero
        eyebrow="GoBID · Operação"
        title="Leilões"
        description="Leilão reverso de frete: publique a carga com preço-alvo e janela, receba lances das transportadoras e feche a vencedora como cotação."
        actions={
          <>
            <Button color="primary" className="btn-sm" onClick={() => setEscolherAberto(true)}>
              + Novo leilão
            </Button>
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
          </>
        }
      />

      <FilterBand
        mode="client"
        searchLabel="Buscar"
        searchPlaceholder="Nº do leilão, cotação, destino ou transportadora..."
        searchValue={searchTerm}
        onSearchChange={(v) => {
          setSearchTerm(v);
          setPage(1);
        }}
        activeFiltersCount={estadoFiltro ? 1 : 0}
        open={mostrarFiltros}
        onToggle={setMostrarFiltros}
        panelTitle="Filtros"
        showActions
        busy={loading}
        onApply={() => setMostrarFiltros(false)}
        onClear={() => {
          setEstadoFiltro(null);
          setSearchTerm("");
          setPage(1);
        }}
      >
        <Row className="ff-filters-row">
          <Col>
            <FormGroup>
              <Label>Estado</Label>
              <Input type="select" value={estadoFiltro || ""} onChange={(e) => toggleEstado(e.target.value || null)}>
                <option value="">Todos</option>
                {ESTADOS_ORDEM.map((k) => (
                  <option key={k} value={k}>
                    {ESTADO_UI[k].label}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>
      </FilterBand>

      <MetricCardGroup title="Situação" description="Clique num card para filtrar a lista; clique de novo para desfazer. Para abrir um leilão, use o botão + Novo leilão no topo ou o Leiloar em Minhas Cotações.">
        {CARDS.map((c) => (
          <MetricCard
            key={c.key}
            label={c.label}
            value={String(cards[c.campo] || 0)}
            helper={c.helper}
            iconClassName={c.icon}
            tone={c.tone}
            active={estadoFiltro === c.key}
            onClick={() => toggleEstado(c.key)}
          />
        ))}
      </MetricCardGroup>

      {resposta ? (
        <MetricCardGroup title={`Resposta dos convidados · ${resposta.dias || RESPOSTA_DIAS} dias`} description="Quantos convidados deram lance ou recusaram, por origem. Quem é de fora da carteira recebe o perfil do embarcador no convite.">
          {RESPOSTA_CARDS.map((c) => {
            const t = textoResposta(resposta[c.key]);
            return <MetricCard key={c.key} label={c.label} value={t.value} helper={t.helper} iconClassName={c.icon} tone={c.tone} />;
          })}
        </MetricCardGroup>
      ) : null}

      <ShellCard title="Leilões" description={`${paginaAtual.total} resultado(s)`}>
        <TableSurface>
          {error ? (
            <div className="lf-empty-state">{error}</div>
          ) : loading && !dados ? (
            <div className="lf-empty-state">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Carregando leilões...
            </div>
          ) : ordenados.length === 0 ? (
            <div className="lf-empty-state">Nenhum leilão para os filtros informados.</div>
          ) : (
            <Table className="ff-table lf-table hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  {columns.map((col) => (
                    <th key={col.field} className={col.numeric ? "is-number" : undefined}>
                      <SortHeader label={col.label} field={col.field} sort={sort} onSort={handleSort} align={col.numeric ? "right" : "left"} />
                    </th>
                  ))}
                  <th>Ação</th>
                </tr>
              </thead>
              <tbody>
                {paginaAtual.items.map((item) => {
                  const ui = ESTADO_UI[item.estado] || ESTADO_UI.cancelada;
                  const cd = item.estado === "aberta" ? countdown(item.fechaEm, offset) : null;
                  return (
                    <tr key={item._id}>
                      <td>#{item.numero}</td>
                      <td>{item.numeroCotacao != null ? `nº ${item.numeroCotacao}` : item.baseNumEmbarque || "-"}</td>
                      <td>{item.destinoTxt}</td>
                      <td className="is-number">{fmtBRL(item.precoAlvo)}</td>
                      <td className="is-number">
                        {item.menorLance != null ? fmtBRL(item.menorLance) : "—"}
                        {item.menorLanceNome ? <div className="lf-muted">{item.menorLanceNome}</div> : null}
                      </td>
                      <td className="is-number">
                        {item.qtdTransportadorasComLance || 0} / {item.qtdConvidados || 0}
                      </td>
                      <td>
                        {cd ? (
                          <span className={`lf-countdown lf-countdown-${cd.level}`} title={`Fecha em ${fmtDataHora(item.fechaEm)}`}>
                            ⏱ {cd.texto}
                          </span>
                        ) : (
                          <span className="lf-muted">{fmtDataHora(item.encerradaEm || item.canceladaEm || item.fechaEm)}</span>
                        )}
                      </td>
                      <td>
                        <span className={`lf-badge lf-badge-${ui.tone}`}>{ui.label}</span>
                        {item.vencedor ? <div className="lf-muted">{item.vencedor.nome}</div> : null}
                      </td>
                      <td>
                        <button type="button" className="lf-btn-abrir" onClick={() => setDetalheId(item._id)}>
                          Abrir
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
          {!error && ordenados.length > 0 ? (
            <TablePagination
              page={paginaAtual.page}
              pageSize={pageSize}
              total={paginaAtual.total}
              pageSizes={PAGE_SIZES}
              itemLabel="leilão(ões)"
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          ) : null}
        </TableSurface>
      </ShellCard>

      <EscolherCotacaoModal
        isOpen={escolherAberto}
        onClose={() => setEscolherAberto(false)}
        empresaId={empresaId}
        onEscolher={(embarqueId) => {
          setEscolherAberto(false);
          setNovoEmbarqueId(embarqueId);
        }}
        onNovaCarga={() => {
          setEscolherAberto(false);
          history.push("/app/gobid/operacao/nova-cotacao?leilao=1");
        }}
      />

      <NovoLeilaoModal
        isOpen={Boolean(novoEmbarqueId)}
        onClose={() => setNovoEmbarqueId(null)}
        empresaId={empresaId}
        embarqueId={novoEmbarqueId}
        onCriado={(criado) => {
          setNovoEmbarqueId(null);
          addToast({ place: "tr", type: "success", title: `Leilão nº ${criado.numero} publicado`, description: `${criado.qtdConvidados} transportadora(s) convidada(s). Fecha em ${fmtDataHora(criado.fechaEm)}.`, icon: "tim-icons icon-check-2" });
          carregar();
          setDetalheId(criado._id);
        }}
        onJaAtivo={(leilaoId, msg) => {
          setNovoEmbarqueId(null);
          addToast({ place: "tr", type: "warning", title: "Já em leilão", description: msg || "Esta cotação já tem um leilão em andamento.", icon: "tim-icons icon-alert-circle-exc" });
          if (leilaoId) setDetalheId(leilaoId);
        }}
      />

      <LeilaoDetalheModal
        isOpen={Boolean(detalheId)}
        onClose={() => {
          setDetalheId(null);
          carregar();
        }}
        empresaId={empresaId}
        leilaoId={detalheId}
        onChanged={(r, meta) => {
          carregar();
          if (meta && meta.reaberto && r && r._id) {
            addToast({ place: "tr", type: "success", title: `Leilão nº ${r.numero} reaberto`, description: `Novos convites enviados. Fecha em ${fmtDataHora(r.fechaEm)}.`, icon: "tim-icons icon-check-2" });
            setDetalheId(r._id);
          }
        }}
      />
    </div>
  );
};

export default Leiloes;
